/**
 * @file   src\pages\Notifications.js
 * @brief  This file is responsible for notifications settings.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getNotificationSettings } from "../actions/userprofile";
import { saveNotifications } from "../services/userprofileService";
import { trackEvent } from "../services/analytics";
import {
  buildSettingsState,
  applyToggleResult,
  isUnsubscribedFromAll,
  buildAllTogglePayload,
  applyAllToggleResult,
  toggleValueForUnsubscribeAll,
  UNSUBSCRIBE_ELIGIBLE_KEYS,
} from "../helpers/notificationSettings";

// One toggle per alert type the user can unsubscribe from individually (see
// the CAN-SPAM unsubscribe links/API - each of these keys is also a category
// in Gitcha-API's notificationCategories whitelist), plus Account activity
// (billing/subscription preference, unrelated to unsubscribe). Grouped for
// display only - each key still saves as its own independent field, so
// toggling one never touches the others (Notification_Settings.model.js
// does a true partial update on save).
const NOTIFICATION_GROUPS = [
  {
    title: "Matches & alerts",
    toggles: [
      {
        key: "Matches",
        label: "New matches",
        description: "Get notified about new matches to your property",
      },
      {
        key: "MatchRecap",
        label: "Match recap",
        description: "Get a periodic recap of your recent matches",
      },
      {
        key: "MlsMatchDigest",
        label: "Daily buyer-demand digest",
        description:
          "Get a daily digest of buyers whose Want-Listings match your MLS listings",
      },
      {
        key: "StandoutMatch",
        label: "Standout match alerts",
        description: "Get notified when a strong property match is found",
      },
      {
        key: "UpgradeNudge",
        label: "Upgrade reminders",
        description: "Get occasional reminders about upgrading your plan",
      },
    ],
  },
  {
    title: "Listing activity",
    toggles: [
      {
        key: "ListingSyndicated",
        label: "Listing syndication",
        description: "Get notified when your Want-Listing is syndicated",
      },
      {
        key: "ListingActivity",
        label: "Listing activity & tips",
        description:
          "Get notified about your listing's status and tips to close more deals",
      },
    ],
  },
  {
    title: "Messages & introductions",
    toggles: [
      {
        key: "Messages",
        label: "Messages",
        description: "Get notified when another member messages me directly",
      },
      {
        key: "Introductions",
        label: "Introductions",
        description: "Get notified when another member sends me an introduction",
      },
    ],
  },
  {
    title: "Account & billing",
    toggles: [
      {
        key: "AccountActivity",
        label: "Account activity",
        description: "Get notified about important account activity",
      },
    ],
  },
];

const Notifications = ({ ...props }) => {
  const dispatch = useDispatch();
  const { userDetails } = props;
  const userNotificationSettings = useSelector(
    (state) => state.userProfile.notificationSettings
  );
  const notificationLoading = useSelector(
    (state) => state.userProfile.notificationLoading
  );
  const notificationError = useSelector(
    (state) => state.userProfile.notificationError
  );
  // Seeded from the same missing-row defaults the API applies (see
  // buildSettingsState) rather than {} - a user whose fetch hasn't
  // resolved yet, or who has never saved a row, is actually receiving
  // these emails today, so the toggles should reflect that instead of
  // flashing all-off.
  const [settings, setSettings] = useState(buildSettingsState());
  // True while ANY save (single toggle or master toggle) is in flight.
  // Blocks a second save from starting so its revert-on-failure snapshot
  // can't be taken from state a still-pending save is about to overwrite.
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(getNotificationSettings());
  }, [userDetails?.UserId]);

  useEffect(() => {
    if (notificationError) return; // keep the last-known-good state on a failed fetch
    setSettings(buildSettingsState(userNotificationSettings?.[0]));
  }, [userNotificationSettings, notificationError]);

  /**
   * Saves a single toggle. Only the changed key is sent - the API does a
   * true partial update, so this never touches any other preference.
   * Optimistically updates, then reverts and shows an error if the save
   * actually failed - a failed unsubscribe must never look like it worked.
   * @param {string} key
   * @param {boolean} value
   */
  const handleToggle = async (key, value) => {
    if (isSaving) return; // a save is already in flight - see isSaving above
    setIsSaving(true);
    const previousValue = !!settings[key];
    setSettings((prev) => ({ ...prev, [key]: value }));

    let saveSucceeded = false;
    try {
      const response = await saveNotifications({ [key]: value });
      saveSucceeded = response?.errorCode === 0;
    } catch (err) {
      saveSucceeded = false;
    }

    setSettings((prev) =>
      applyToggleResult(prev, key, value, previousValue, saveSucceeded)
    );
    setIsSaving(false);

    if (saveSucceeded) {
      toast.dismiss();
      toast.success("Notification settings saved successfully.");
    } else {
      toast.dismiss();
      toast.error(
        "Something went wrong saving your preference. Please try again."
      );
      // No dashboard currently surfaces individual toast errors - this is
      // the only signal that a deploy-ordering mismatch (this PR live
      // before the API's partial-update support) is breaking every save.
      trackEvent({
        name: "notification_settings_save_failed",
        attributes: { key, scope: "single" },
      });
    }
  };

  const allUnsubscribed = isUnsubscribedFromAll(settings);

  /**
   * Turns every unsubscribe-eligible toggle on or off in a single save,
   * instead of requiring nine individual clicks. Account activity (billing)
   * is never touched - see UNSUBSCRIBE_ELIGIBLE_KEYS. Same optimistic
   * update / revert-on-failure shape as handleToggle, but captures each
   * toggle's own previous value first so a failed save reverts exactly
   * rather than assuming they were all on before.
   *
   * Takes the checkbox's target *checked* state (checked = "unsubscribed
   * from all"), NOT the per-category toggle value - those are inverses of
   * each other, and `toggleValueForUnsubscribeAll` is the single place
   * that conversion happens. A previous version of this function took the
   * toggle value directly and inverted inline at the call site, which got
   * the direction backwards (see notificationSettings.js for the full
   * story) - route any future caller through the named helper instead of
   * re-deriving the inversion here.
   * @param {boolean} shouldUnsubscribeAll
   */
  const handleToggleAll = async (shouldUnsubscribeAll) => {
    if (isSaving) return; // a save is already in flight - see isSaving above
    setIsSaving(true);
    const toggleValue = toggleValueForUnsubscribeAll(shouldUnsubscribeAll);
    const previousValues = UNSUBSCRIBE_ELIGIBLE_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: !!settings[key] }),
      {}
    );
    setSettings((prev) => ({
      ...prev,
      ...buildAllTogglePayload(toggleValue),
    }));

    let saveSucceeded = false;
    try {
      const response = await saveNotifications(
        buildAllTogglePayload(toggleValue)
      );
      saveSucceeded = response?.errorCode === 0;
    } catch (err) {
      saveSucceeded = false;
    }

    setSettings((prev) =>
      applyAllToggleResult(prev, previousValues, toggleValue, saveSucceeded)
    );
    setIsSaving(false);

    if (saveSucceeded) {
      toast.dismiss();
      toast.success(
        shouldUnsubscribeAll
          ? "Unsubscribed from all email notifications."
          : "Notification settings saved successfully."
      );
    } else {
      toast.dismiss();
      toast.error(
        "Something went wrong saving your preference. Please try again."
      );
      trackEvent({
        name: "notification_settings_save_failed",
        attributes: { scope: "all" },
      });
    }
  };

  return (
    <div className="notification-wrap">
      <div className="title-sec">
        <h4>Email notifications</h4>
      </div>
      {notificationError && (
        <p
          className="error-msg"
          role="alert"
          style={{ height: "auto", textTransform: "none" }}
        >
          Couldn't load your current notification settings. Toggles below
          may not reflect what's saved - please refresh before making
          changes.
        </p>
      )}
      <div className="notification-group notification-master-toggle">
        <div className="field-wrap">
          <label className="form-check-label checkbox-label">
            <p>Unsubscribe from all email notifications</p>
            <span id="unsubscribe-all-description">
              Turn off every alert type below in one step. Account activity
              (billing) emails are unaffected.
            </span>
            <input
              className="form-check-input"
              type="checkbox"
              checked={allUnsubscribed}
              disabled={notificationLoading || !!notificationError || isSaving}
              aria-describedby="unsubscribe-all-description"
              onChange={() => handleToggleAll(!allUnsubscribed)}
              id="unsubscribeAllCheck"
            />
          </label>
        </div>
      </div>
      {NOTIFICATION_GROUPS.map((group) => (
        <div className="notification-group" key={group.title}>
          <h5 className="notification-group-title">{group.title}</h5>
          {group.toggles.map((toggle) => {
            const descriptionId = `${toggle.key}-description`;
            return (
              <div className="field-wrap" key={toggle.key}>
                <label className="form-check-label checkbox-label">
                  <p>{toggle.label}</p>
                  <span id={descriptionId}>{toggle.description}</span>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={!!settings[toggle.key]}
                    disabled={notificationLoading || !!notificationError || isSaving}
                    aria-describedby={descriptionId}
                    onChange={() =>
                      handleToggle(toggle.key, !settings[toggle.key])
                    }
                    id={`${toggle.key}check`}
                  />
                </label>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Notifications;
